import React, { useState, useEffect } from "react";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "cookie_consent";

export default function CookieConsent() {
    const [visible, setVisible] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [analyticsOn, setAnalyticsOn] = useState(false);

    useEffect(() => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            if (!saved) {
                // small delay so it doesn't pop in instantly
                const t = setTimeout(() => setVisible(true), 600);
                return () => clearTimeout(t);
            }
        } catch (e) {
            setVisible(true);
        }
    }, []);

    const persist = (analytics) => {
        try {
            localStorage.setItem(
                STORAGE_KEY,
                JSON.stringify({
                    necessary: true,
                    analytics: !!analytics,
                    timestamp: new Date().toISOString(),
                    version: "1.0",
                })
            );
        } catch (e) {
            // ignore
        }
        setVisible(false);
        setShowSettings(false);
    };

    const acceptAll = () => persist(true);
    const rejectAll = () => persist(false);
    const savePreferences = () => persist(analyticsOn);

    const reopen = () => {
        setShowSettings(true);
        setVisible(true);
    };

    if (!visible) {
        // expose a tiny floating button only when settings link is requested via global event
        return (
            <button
                data-testid="cookie-settings-trigger"
                onClick={reopen}
                aria-label="Impostazioni cookie"
                className="hidden"
                id="cookie-reopen-btn"
            />
        );
    }

    return (
        <div
            data-testid="cookie-consent-banner"
            role="dialog"
            aria-live="polite"
            aria-label="Banner cookie"
            className="fixed inset-x-0 bottom-0 z-[1000] px-4 sm:px-6 pb-4 sm:pb-6 pointer-events-none"
        >
            <div className="max-w-4xl mx-auto pointer-events-auto bg-ink-950/95 backdrop-blur-xl border border-gold/30 shadow-2xl">
                <div className="p-5 sm:p-7">
                    <div className="flex items-start gap-4">
                        <div className="hidden sm:flex w-11 h-11 items-center justify-center border border-gold/40 bg-gold/5 shrink-0">
                            <Cookie size={20} className="text-gold" />
                        </div>
                        <div className="flex-1">
                            <div className="flex items-center justify-between gap-3">
                                <div className="font-serif text-lg sm:text-xl text-ink-50">Rispettiamo la tua privacy</div>
                                <button
                                    data-testid="cookie-close-btn"
                                    onClick={rejectAll}
                                    aria-label="Chiudi e rifiuta cookie non necessari"
                                    className="text-ink-400 hover:text-ink-100 transition-colors"
                                >
                                    <X size={18} />
                                </button>
                            </div>
                            <p className="text-sm text-ink-100 leading-relaxed mt-2">
                                Usiamo <strong>cookie tecnici</strong> per il funzionamento del sito (sempre attivi) e, solo se acconsenti, <strong>cookie analitici</strong> per migliorare il servizio. Nessun cookie pubblicitario, nessuna profilazione.{" "}
                                <a href="/cookie" className="text-gold hover:underline">Leggi la cookie policy</a>.
                            </p>

                            {showSettings && (
                                <div className="mt-5 space-y-3" data-testid="cookie-settings">
                                    <div className="border border-white/10 p-4 flex items-center justify-between gap-3">
                                        <div>
                                            <div className="text-sm text-ink-50 font-medium">Cookie tecnici</div>
                                            <div className="text-xs text-ink-400 mt-1">Necessari al funzionamento. Sempre attivi.</div>
                                        </div>
                                        <span className="text-xs text-gold uppercase tracking-wider">Sempre on</span>
                                    </div>
                                    <label className="border border-white/10 p-4 flex items-center justify-between gap-3 cursor-pointer hover:border-gold/40 transition-colors">
                                        <div>
                                            <div className="text-sm text-ink-50 font-medium">Cookie analitici</div>
                                            <div className="text-xs text-ink-400 mt-1">Statistiche aggregate e anonime sull&apos;uso del sito.</div>
                                        </div>
                                        <input
                                            data-testid="cookie-analytics-toggle"
                                            type="checkbox"
                                            checked={analyticsOn}
                                            onChange={(e) => setAnalyticsOn(e.target.checked)}
                                            className="w-5 h-5 accent-amber-500"
                                        />
                                    </label>
                                </div>
                            )}

                            <div className="mt-5 flex flex-col sm:flex-row gap-2 sm:gap-3">
                                <button
                                    data-testid="cookie-accept-all-btn"
                                    onClick={acceptAll}
                                    className="px-5 py-2.5 bg-gold text-ink-950 text-sm font-medium hover:bg-amber-300 transition-colors"
                                >
                                    Accetta tutti
                                </button>
                                <button
                                    data-testid="cookie-reject-all-btn"
                                    onClick={rejectAll}
                                    className="px-5 py-2.5 border border-white/20 text-ink-100 text-sm hover:border-gold hover:text-gold transition-colors"
                                >
                                    Rifiuta non necessari
                                </button>
                                {!showSettings ? (
                                    <button
                                        data-testid="cookie-settings-btn"
                                        onClick={() => setShowSettings(true)}
                                        className="px-5 py-2.5 text-sm text-ink-400 hover:text-gold transition-colors sm:ml-auto"
                                    >
                                        Personalizza
                                    </button>
                                ) : (
                                    <button
                                        data-testid="cookie-save-btn"
                                        onClick={savePreferences}
                                        className="px-5 py-2.5 border border-gold/50 text-gold text-sm hover:bg-gold hover:text-ink-950 transition-colors sm:ml-auto"
                                    >
                                        Salva preferenze
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// Helper exposed for footer link
export const openCookieSettings = () => {
    try {
        localStorage.removeItem(STORAGE_KEY);
        window.location.reload();
    } catch (e) {
        window.location.reload();
    }
};
